import React, { useState, useEffect } from 'react';
import { 
  CheckCircle2, 
  Save, 
  XCircle, 
  Flag,
  Lock,
  User,
  Calendar
} from 'lucide-react';
import { StagingRecord, FieldSchema, ValidationRule, RecordData } from '../../types';
import { useValidation } from '../../hooks/useValidation';
import { SchemaField } from '../molecules/SchemaField';

interface ResolutionEditorProps {
  record: StagingRecord;
  schema: FieldSchema[];
  rules: ValidationRule[];
  onSave: (id: string, updatedData: RecordData, comment: string) => void;
  onReject: (id: string, reason: string) => void;
  readOnly?: boolean;
}

export const ResolutionEditor: React.FC<ResolutionEditorProps> = ({
  record,
  schema,
  rules,
  onSave,
  onReject,
  readOnly = false
}) => {
  const [formData, setFormData] = useState<RecordData>(record.data);
  const [overrides, setOverrides] = useState<Set<string>>(new Set());
  const [rejectReason, setRejectReason] = useState('');
  const [importComment, setImportComment] = useState('');
  const [isRejectMode, setIsRejectMode] = useState(false);

  // Reset state when record changes
  useEffect(() => {
    setFormData(record.data);
    setOverrides(new Set());
    setRejectReason('');
    setImportComment('');
    setIsRejectMode(false);
  }, [record]);

  // Validation Logic via Hook
  const validationErrors = useValidation(formData, rules);

  const activeErrors = Object.keys(validationErrors).filter(key => !overrides.has(key));
  const hasActiveErrors = activeErrors.length > 0;
  const isReady = !hasActiveErrors;

  // Handlers
  const handleFieldChange = (key: string, value: any) => {
    if (readOnly) return;
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const toggleOverride = (key: string) => {
    if (readOnly) return;
    setOverrides(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleSaveClick = () => {
    if (readOnly) return;
    onSave(record.id, formData, importComment);
  };

  const handleRejectClick = () => {
    if (readOnly) return;
    setIsRejectMode(true);
  };

  const confirmReject = () => {
    if (!rejectReason.trim()) return;
    onReject(record.id, rejectReason);
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      
      {/* HEADER */}
      <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
        <div>
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            Record <span className="font-mono text-slate-600">#{record.id}</span>
            {readOnly && <Lock className="w-4 h-4 text-slate-400" />}
          </h2>
          <p className="text-xs text-slate-400 mt-1 font-mono">Imported on {record.importDate}</p>
        </div>
        <div>
          {record.status === 'imported' ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
              Imported
            </span>
          ) : record.status === 'rejected' ? (
             <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
              <XCircle className="w-3.5 h-3.5 mr-1.5" />
              Rejected
            </span>
          ) : isReady ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
              Ready to Import
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
              <Flag className="w-3.5 h-3.5 mr-1.5" />
              Needs Attention
            </span>
          )}
        </div>
      </div>

      {/* BODY - FORM */}
      <div className="flex-1 overflow-y-auto p-6 relative bg-white">
        {readOnly && (
            <div className="absolute top-0 left-0 right-0 z-10 pointer-events-none h-4 bg-gradient-to-b from-white to-transparent"></div>
        )}
        <div className="max-w-2xl mx-auto">
          {schema.map(field => (
            <SchemaField
              key={field.key}
              field={field}
              value={formData[field.key]}
              onChange={(val) => handleFieldChange(field.key, val)}
              error={validationErrors[field.key]}
              isOverridden={overrides.has(field.key)}
              onToggleOverride={() => toggleOverride(field.key)}
              readOnly={readOnly}
            />
          ))}
        </div>
      </div>

      {/* FOOTER - ACTIONS */}
      <div className={`px-6 py-4 border-t transition-colors duration-300 ${
        readOnly ? 'bg-slate-50 border-slate-200' : 
        hasActiveErrors ? 'bg-red-50/30 border-red-100' : 'bg-slate-50 border-slate-200'
      }`}>
        {readOnly ? (
           <div className="flex flex-col space-y-3">
             <div className="flex items-start">
                <div className={`p-2 rounded-full mr-3 shrink-0 ${record.status === 'imported' ? 'bg-teal-100' : 'bg-red-100'}`}>
                  {record.status === 'imported' ? (
                     <CheckCircle2 className="w-5 h-5 text-teal-600" />
                  ) : (
                     <XCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>
                
                <div className="flex-1">
                   <h3 className="text-sm font-bold text-slate-900">
                     {record.status === 'imported' ? 'Record Imported' : 'Record Rejected'}
                   </h3>
                   <div className="text-xs text-slate-500 mt-1 flex items-center flex-wrap gap-1">
                     {record.resolvedBy === 'System' || !record.resolvedBy ? (
                        <span className="bg-slate-200 px-1.5 py-0.5 rounded text-slate-700 font-medium text-[10px] uppercase tracking-wide">Auto-processed</span>
                     ) : (
                        <span className="flex items-center">
                          <User className="w-3 h-3 mr-1" />
                          Action by <span className="font-medium text-slate-700 ml-1">{record.resolvedBy}</span>
                        </span>
                     )}
                     {record.resolutionDate && (
                       <span className="flex items-center text-slate-400 ml-2 border-l border-slate-300 pl-2">
                         <Calendar className="w-3 h-3 mr-1" />
                         <span className="font-mono">{new Date(record.resolutionDate).toLocaleString()}</span>
                       </span>
                     )}
                   </div>
                </div>
             </div>
             
             {record.resolutionNote ? (
               <div className="ml-12 bg-white border border-slate-200 rounded-lg p-3 text-sm text-slate-600 shadow-sm relative">
                 <div className="absolute top-0 left-4 -mt-1.5 w-3 h-3 bg-white border-t border-l border-slate-200 transform rotate-45"></div>
                 <span className="font-bold text-[10px] text-slate-400 uppercase tracking-wider block mb-1">
                   {record.status === 'imported' ? 'Import Note' : 'Rejection Reason'}
                 </span>
                 {record.resolutionNote}
               </div>
             ) : record.status === 'imported' && (record.resolvedBy === 'System' || !record.resolvedBy) ? (
                 <div className="ml-12 text-xs text-slate-400 italic">
                    This record passed all validation checks and was automatically imported.
                 </div>
             ) : null}
           </div>
        ) : hasActiveErrors ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center text-red-700 font-medium text-sm">
              <XCircle className="w-5 h-5 mr-2" />
              {activeErrors.length} Unresolved Issue{activeErrors.length !== 1 && 's'}
            </div>
            <button
              onClick={handleRejectClick}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium shadow-sm transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-red-500 text-sm"
            >
              Reject Record
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Audit Note / Import Comment</label>
              <input
                type="text"
                value={importComment}
                onChange={(e) => setImportComment(e.target.value)}
                placeholder="Optional comment for the audit log..."
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-teal-500 focus:border-teal-500 bg-white text-sm"
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-emerald-700 font-medium text-sm flex items-center">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                All validations passed {overrides.size > 0 && `(${overrides.size} overridden)`}
              </span>
              <button
                onClick={handleSaveClick}
                className="flex items-center px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium shadow-sm shadow-teal-600/20 transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 text-sm"
              >
                <Save className="w-4 h-4 mr-2" />
                Save & Import
              </button>
            </div>
          </div>
        )}
      </div>

      {/* REJECT MODAL OVERLAY */}
      {isRejectMode && (
        <div className="absolute inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200 border border-slate-200">
            <h3 className="text-lg font-bold text-slate-900 mb-2">Reject Record <span className="font-mono text-base font-normal text-slate-500">#{record.id}</span></h3>
            <p className="text-sm text-slate-500 mb-4">
              Please provide a reason for rejecting this record. This will be logged in the system.
            </p>
            <textarea
              className="w-full h-32 px-3 py-2 border border-slate-300 rounded-lg mb-4 focus:ring-1 focus:ring-red-500 focus:border-red-500 resize-none text-sm placeholder-slate-400"
              placeholder="Reason for rejection..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              autoFocus
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setIsRejectMode(false)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 rounded-lg font-medium text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmReject}
                disabled={!rejectReason.trim()}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm shadow-sm transition-colors"
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};